import { useViewportSize } from '@react-aria/utils';
import { useState, useEffect, useMemo } from 'react';
import { useDebouncedValue } from './useDebouncedValue.js';

// Horizontal breakpoints based on common responsive
// design standards.
// Compact: mobile devices (< 667px)
// Regular: tablets and small desktops (667px - 1279px)
// Expanded: large desktops (>= 1280px)
const BREAKPOINT_HORIZONTAL_REGULAR = 667;
const BREAKPOINT_HORIZONTAL_EXPANDED = 1280;
// Vertical breakpoints based on common responsive
// design standards.
// Compact: mobile devices (< 667px)
// Regular: tablets and small desktops (667px - 1279px)
// Expanded: large desktops (>= 1280px)
const BREAKPOINT_VERTICAL_REGULAR = 667;
const BREAKPOINT_VERTICAL_EXPANDED = 1280;
/**
 * Custom hook that returns a size class based on the current viewport width.
 * Uses React Aria's useViewportSize to track viewport dimensions.
 *
 * @returns {SizeClass} - One of "compact", "regular", or "expanded"
 *
 * @example
 * ```tsx
 * const sizeClass = useSizeClass();
 * // Returns "compact" for width < 768px
 * // Returns "regular" for width 768px - 1279px
 * // Returns "expanded" for width >= 1280px
 * ```
 */
function useHorizontalSizeClass() {
    const { width: rawWidth } = useViewportSize();
    // Debounce the raw width changes so frequent resize events don’t re-render
    const debouncedWidth = useDebouncedValue(rawWidth, 250);
    const [sizeClass, setSizeClass] = useState(() => {
        if (rawWidth < BREAKPOINT_HORIZONTAL_REGULAR)
            return 'compact';
        if (rawWidth < BREAKPOINT_HORIZONTAL_EXPANDED)
            return 'regular';
        return 'expanded';
    });
    useEffect(() => {
        if (debouncedWidth < BREAKPOINT_HORIZONTAL_REGULAR) {
            setSizeClass('compact');
        }
        else if (debouncedWidth < BREAKPOINT_HORIZONTAL_EXPANDED) {
            setSizeClass('regular');
        }
        else {
            setSizeClass('expanded');
        }
    }, [debouncedWidth]);
    return sizeClass;
}
/**
 * Custom hook that returns the initial horizontal size class based on the
 * viewport width at the time of the first render.
 *
 * @returns {SizeClass} - One of "compact", "regular", or "expanded"
 *
 * @example
 * ```tsx
 * const initialSizeClass = useInitialHorizontalSizeClass();
 * // Returns "compact" for width < 768px
 * // Returns "regular" for width 768px - 1279px
 * // Returns "expanded" for width >= 1280px
 * ```
 */
function useInitialHorizontalSizeClass() {
    const [width] = useState(() => window.innerWidth);
    return useMemo(() => {
        if (width < BREAKPOINT_HORIZONTAL_REGULAR)
            return 'compact';
        if (width < BREAKPOINT_HORIZONTAL_EXPANDED)
            return 'regular';
        return 'expanded';
    }, []);
}
/**
 * Custom hook that returns a size class based on the current viewport height.
 * Uses React Aria's useViewportSize to track viewport dimensions.
 *
 * @returns {SizeClass} - One of "compact", "regular", or "expanded"
 *
 * @example
 * ```tsx
 * const sizeClass = useVerticalSizeClass();
 * // Returns "compact" for height < 667px
 * // Returns "regular" for height 667px - 1279px
 * // Returns "expanded" for height >= 1280px
 * ```
 */
function useVerticalSizeClass() {
    const { height: rawHeight } = useViewportSize();
    // Debounce raw height changes to avoid excessive rerenders
    const debouncedHeight = useDebouncedValue(rawHeight, 250);
    const [sizeClass, setSizeClass] = useState(() => {
        if (rawHeight < BREAKPOINT_VERTICAL_REGULAR)
            return 'compact';
        if (rawHeight < BREAKPOINT_VERTICAL_EXPANDED)
            return 'regular';
        return 'expanded';
    });
    useEffect(() => {
        if (debouncedHeight < BREAKPOINT_VERTICAL_REGULAR) {
            setSizeClass('compact');
        }
        else if (debouncedHeight < BREAKPOINT_VERTICAL_EXPANDED) {
            setSizeClass('regular');
        }
        else {
            setSizeClass('expanded');
        }
    }, [debouncedHeight]);
    return sizeClass;
}
/**
 * Custom hook that returns the initial vertical size class based on the
 * viewport height at the time of the first render.
 *
 * @returns {SizeClass} - One of "compact", "regular", or "expanded"
 *
 * @example
 * ```tsx
 * const initialSizeClass = useInitialVerticalSizeClass();
 * // Returns "compact" for height < 667px
 * // Returns "regular" for height 667px - 1279px
 * // Returns "expanded" for height >= 1280px
 * ```
 */
function useInitialVerticalSizeClass() {
    const [height] = useState(() => window.innerHeight);
    return useMemo(() => {
        if (height < BREAKPOINT_VERTICAL_REGULAR)
            return 'compact';
        if (height < BREAKPOINT_VERTICAL_EXPANDED)
            return 'regular';
        return 'expanded';
    }, []);
}
/**
 * Custom hook that returns both horizontal and vertical size classes.
 * Uses React Aria's useViewportSize to track viewport dimensions.
 *
 * @returns {SizeClasses} - Object containing horizontal and vertical size classes
 *
 * @example
 * ```tsx
 * const { horizontal, vertical } = useSizeClass();
 * // horizontal: "compact" | "regular" | "expanded" based on width
 * // vertical: "compact" | "regular" | "expanded" based on height
 * ```
 */
function useSizeClass() {
    const { width: rawWidth, height: rawHeight } = useViewportSize();
    const debouncedWidth = useDebouncedValue(rawWidth, 250);
    const debouncedHeight = useDebouncedValue(rawHeight, 250);
    const [sizeClasses, setSizeClasses] = useState(() => ({
        horizontal: rawWidth < BREAKPOINT_HORIZONTAL_REGULAR
            ? 'compact'
            : rawWidth < BREAKPOINT_HORIZONTAL_EXPANDED
                ? 'regular'
                : 'expanded',
        vertical: rawHeight < BREAKPOINT_VERTICAL_REGULAR
            ? 'compact'
            : rawHeight < BREAKPOINT_VERTICAL_EXPANDED
                ? 'regular'
                : 'expanded',
    }));
    useEffect(() => {
        setSizeClasses({
            horizontal: debouncedWidth < BREAKPOINT_HORIZONTAL_REGULAR
                ? 'compact'
                : debouncedWidth < BREAKPOINT_HORIZONTAL_EXPANDED
                    ? 'regular'
                    : 'expanded',
            vertical: debouncedHeight < BREAKPOINT_VERTICAL_REGULAR
                ? 'compact'
                : debouncedHeight < BREAKPOINT_VERTICAL_EXPANDED
                    ? 'regular'
                    : 'expanded',
        });
    }, [debouncedWidth, debouncedHeight]);
    return sizeClasses;
}

export { useHorizontalSizeClass, useInitialHorizontalSizeClass, useInitialVerticalSizeClass, useSizeClass, useVerticalSizeClass };
//# sourceMappingURL=useSizeClass.js.map
